import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, articles } from '@/mocks/homeData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="mt-6 text-2xl font-heading font-bold text-foreground-900">分类不存在</h1>
            <p className="mt-2 text-foreground-500">你要找的分类好像走丢了~</p>
            <Link
              to="/"
              className="inline-block mt-6 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  return (
    <>
      <Navbar />
      <main>
        {/* Category Header */}
        <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${category.image})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/25 to-black/55"></div>
          <div className="absolute inset-0 flex items-end">
            <div className="container-custom pb-10">
              {/* Breadcrumb */}
              <nav className="mb-3" aria-label="面包屑导航">
                <ol className="flex items-center gap-2 text-xs text-white/70">
                  <li>
                    <Link to="/" className="hover:text-white transition-colors cursor-pointer">
                      首页
                    </Link>
                  </li>
                  <li className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </li>
                  <li className="text-white">{category.name}</li>
                </ol>
              </nav>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">
                {category.name}
              </h1>
              <p className="mt-2 text-sm md:text-base text-white/70 max-w-xl">
                {category.description}
              </p>
              <span className="inline-block mt-3 text-xs text-white/60 bg-white/15 px-3 py-1 rounded-full">
                共 {categoryArticles.length} 篇文章
              </span>
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="container-custom">
            {categoryArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="mt-4 text-foreground-500">这个分类下还没有文章，敬请期待~</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      {article.featured && (
                        <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-accent-500 text-white text-xs font-medium whitespace-nowrap">
                          精选
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {article.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-base font-heading font-bold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-background-200/70">
                        <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                          <img
                            src={article.authorAvatar}
                            alt={article.author}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-xs text-foreground-500">{article.author}</span>
                        <span className="text-xs text-foreground-400 ml-auto">
                          {article.publishDate} · {article.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'BreadcrumbList',
                '@id': `https://ruilongguoyao.cn/category/${slug}/#breadcrumb`,
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: '首页',
                    item: 'https://ruilongguoyao.cn/',
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: category.name,
                    item: `https://ruilongguoyao.cn/category/${slug}/`,
                  },
                ],
              },
              {
                '@type': 'CollectionPage',
                '@id': `https://ruilongguoyao.cn/category/${slug}/#collection`,
                url: `https://ruilongguoyao.cn/category/${slug}/`,
                name: `${category.name} - 少女领域`,
                description: category.description,
                isPartOf: {
                  '@id': 'https://ruilongguoyao.cn/#website',
                },
                about: category.name,
                inLanguage: 'zh-CN',
              },
            ],
          }),
        }}
      />
    </>
  );
}